import {
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query';

export type JotWorkerQueryOptions = { access?: 'public' };

export function createJotWorkerBaseQuery(
  apiUrl: string | undefined,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, JotWorkerQueryOptions> {
  const baseUrl = apiUrl?.trim();
  let validUrl = false;
  try {
    const url = new URL(baseUrl || '');
    validUrl =
      ['http:', 'https:'].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash;
  } catch {
    /* Configuration errors are returned when an endpoint is called. */
  }

  const request = fetchBaseQuery({
    baseUrl,
    timeout: 10000,
    credentials: 'omit',
    prepareHeaders: headers => {
      headers.delete('authorization');
      headers.delete('cookie');
      return headers;
    },
  });
  return (args, api, options) => {
    if (!validUrl) {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'Configure VITE_JOT_WORKER_API_URL with the JotWorker HTTP origin.',
        },
      };
    }
    if (options?.access !== 'public') {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'This JotWorker endpoint has not declared public access.',
        },
      };
    }
    const fetchArgs = typeof args === 'string' ? { url: args } : args;
    if (/^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(fetchArgs.url)) {
      return {
        error: { status: 'CUSTOM_ERROR', error: 'JotWorker endpoints must use relative paths.' },
      };
    }
    return request({ ...fetchArgs, credentials: 'omit' }, api, {});
  };
}

export const jotWorkerBaseQuery = createJotWorkerBaseQuery(import.meta.env.VITE_JOT_WORKER_API_URL);
